import { Suspense, useMemo } from 'react';
import { useMe } from '../lib/me';
import { navigate } from '../lib/router';
import { toolApi } from '../lib/api';
import { TEMPLATE_CLIENTS, TOOL_CLIENTS } from '../../tools/registry.client';
import { Badge, Loading, PageHeader } from '../components/ui';
import { FavoriteStar } from '../components/FavoriteStar';
import { PageClearanceButton } from '../components/PageClearance';
import { NotFound } from './NotFound';

export function ToolHost({ toolId, subPath }: { toolId: string; subPath: string }) {
  const { me, can } = useMe();
  const tool = me?.nav.flatMap((s) => s.tools).find((t) => t.id === toolId);
  const api = useMemo(() => toolApi(toolId), [toolId]);
  const Component = TOOL_CLIENTS[toolId] ?? (tool?.template ? TEMPLATE_CLIENTS[tool.template] : undefined);
  if (!me || !tool || !Component) return <NotFound />;
  const section = me.nav.find((s) => s.tools.some((t) => t.id === toolId));
  return (
    <div className="tool-host">
      {tool.template ? (
        // Generated pages (unit pages) carry their own header; keep the shared controls in a slim bar.
        <div className="tool-host-bar">
          {section && <span className="eyebrow">{section.name}</span>}
          <div className="grow" />
          <>
            {tool.status === 'beta' && <Badge tone="amber">beta</Badge>}
            {tool.placeholder ? <Badge>coming soon</Badge> : <Badge>v{tool.version}</Badge>}
            <FavoriteStar toolId={tool.id} toolName={tool.name} withLabel />
            <PageClearanceButton node={`tool:${tool.id}`} label={tool.name} />
          </>
        </div>
      ) : (
        <PageHeader eyebrow={section?.layout === 'dashboard' ? section.name : section?.group === 'management' ? 'Management' : undefined}
        title={tool.tagline ? `${tool.name} · ${tool.tagline}` : tool.name} subtitle={tool.description}
        actions={<>
          {tool.status === 'beta' && <Badge tone="amber">beta</Badge>}
          {tool.placeholder ? <Badge>coming soon</Badge> : <Badge>v{tool.version}</Badge>}
          <FavoriteStar toolId={tool.id} toolName={tool.name} withLabel />
          <PageClearanceButton node={`tool:${tool.id}`} label={tool.name} />
        </>} />
      )}
      <Suspense fallback={<Loading />}>
        <Component tool={tool} me={me} can={can} api={api} subPath={subPath} go={(p: string) => navigate(`/t/${toolId}${p ? `/${p}` : ''}`)} />
      </Suspense>
    </div>
  );
}
